import React, { useCallback, useMemo, useState } from 'react';
import { Button, Form, message, Typography } from 'antd';
import type { FormProps } from 'antd';
import type {
  AdminProductCreateFormProps,
  AdminProductFormValues,
  AdminProductValidationSummary
} from '../../interfaces';
import AdminProductMaterialsFields from '../AdminProductMaterialsFields';
import AdminProductValidationAlert from './AdminProductValidationAlert';
import {
  BasicInformationSection,
  DefaultSizeGridSection,
  ProductDetailsCollapse,
  ProductSetupSection,
} from './AdminProductCreateSections';
import { buildAdminProductValidationSummary } from './adminProductCreateValidation';
import { useCreateFormSizeSync } from './useCreateFormSizeSync';
import './AdminProductCreateForm.scss';

const { Title, Text } = Typography;

const AdminProductCreateForm: React.FC<AdminProductCreateFormProps> = ({
  form,
  types,
  materialTemplates = [],
  filteredSizes,
  selectedTypeRequiresSizes,
  submitting,
  onSubmit,
}) => {
  const [validationSummary, setValidationSummary] = useState<AdminProductValidationSummary | null>(null);
  const filteredSizeIds = useMemo(
    () => filteredSizes.map((size) => size.id),
    [filteredSizes]
  );
  const filteredSizeKey = filteredSizeIds.join('|');
  const selectedGender = Form.useWatch('gender', form);
  const selectedProductTypeId = Form.useWatch('productTypeId', form);
  const selectedSizeIds = Form.useWatch('sizeIds', form);
  const selectedMaterials = Form.useWatch('materials', form);
  const isProductClassified = Boolean(selectedGender && selectedProductTypeId);
  const isSizeStepReady = !selectedTypeRequiresSizes || Boolean(selectedSizeIds?.length);
  const canConfigureMaterials = isProductClassified && isSizeStepReady;
  const hasMaterials = Boolean(selectedMaterials?.length);
  const invalidMaterialIndexes = validationSummary?.invalidMaterialIndexes || [];

  useCreateFormSizeSync({
    filteredSizeIds,
    filteredSizeKey,
    form,
    selectedTypeRequiresSizes,
  });

  const handleSubmit = useCallback((values: AdminProductFormValues) => {
    setValidationSummary(null);
    return onSubmit(values);
  }, [onSubmit]);

  const handleFinishFailed: NonNullable<FormProps<AdminProductFormValues>['onFinishFailed']> = useCallback((errorInfo) => {
    const summary = buildAdminProductValidationSummary(errorInfo.errorFields, form.getFieldsValue(true));
    const firstErrorName = errorInfo.errorFields[0]?.name;

    setValidationSummary(summary);
    message.warning('Complete the required product fields before creating.');

    if (firstErrorName) {
      requestAnimationFrame(() => {
        form.scrollToField(firstErrorName, { behavior: 'smooth', block: 'center' });
      });
    }
  }, [form]);

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      onFinishFailed={handleFinishFailed}
      onClick={(event) => event.stopPropagation()}
      initialValues={{ gender: 'unisex', price: 0, materials: [] }}
    >
    <AdminProductValidationAlert summary={validationSummary} />

    <ProductSetupSection types={types} />

    {isProductClassified && (
      <BasicInformationSection />
    )}

    {isProductClassified && selectedTypeRequiresSizes && (
      <DefaultSizeGridSection
        filteredSizes={filteredSizes}
        filteredSizeIds={filteredSizeIds}
        selectedTypeRequiresSizes={selectedTypeRequiresSizes}
      />
    )}

    {canConfigureMaterials && (
      <div className="admin-product-create__section">
        <Title level={5}>Material Varieties</Title>
        <Text type="secondary" className="admin-product-form__hint">
          Add the material rows first. Open a material only when you need to edit images, sizes, or stock.
        </Text>
        <AdminProductMaterialsFields
          materialTemplates={materialTemplates}
          filteredSizes={filteredSizes}
          invalidMaterialIndexes={invalidMaterialIndexes}
          requiresSizes={selectedTypeRequiresSizes}
        />
      </div>
    )}

    {canConfigureMaterials && hasMaterials && (
      <ProductDetailsCollapse />
    )}
    <Form.Item>
      <Button type="primary" htmlType="submit" loading={submitting} block>
        Create Product
      </Button>
    </Form.Item>
    </Form>
  );
};

export default AdminProductCreateForm;
